<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useCoachingOfferStore } from '@stores/coachingOffer'
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const EditForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/CoachingOffers/EditForm'));

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const coachingOfferStore = useCoachingOfferStore();

const offerId = parseInt(route.params.offerId);
const offer = computed(() => coachingOfferStore.getOfferById(offerId));

// Vrai si l'utilisateur a été redirigé après avoir créé une nouvelle offre
const fromCreation = computed(() => (route.query['from-create'] || null) !== null);
const creationSuccessMessageRef = ref(null);

// Vrai si l'offre a été mise à jour
const offerSaved = ref(false);
const updateSuccessMessageRef = ref(null);

// Référence sur l'élément DOM du message de succès
const editionSuccessMessageRef = ref(null);

const currentApplication = computed(() => applicationStore.getCurrent);

// ready est vrai une fois l'offre chargée.
// Cela permet d'afficher un loader le temps du chargement des données
const ready = ref(false);

// Vrai s'il y a eu une erreur pendant le chargement de l'offre
const loadingOfferError = ref(false);

const { authUser, authUserHasPermission } = useAuthUser({});

const pageTitle = ref(null);
const pageDescription = ref(null);

// Redirection vers une page d'erreur
const redirectNotFound = (() => {
    router.push({
        name: 'error',
        params: {
            statusCode: 404
        }
    });
});

// Méthode appelée si l'utilisateur vient de créer une nouvelle offre
const onCreationSuccess = (() => {
    setTimeout(() => {
        if(creationSuccessMessageRef.value !== null) {
            creationSuccessMessageRef.value.scrollIntoView({ behavior: 'smooth' });
        }
    }, 200);

    setTimeout(() => {
        // Suppression du paramètre en query
        const query = Object.assign({}, route.query);
        delete query['from-create'];
        router.replace({
            query
        });

    }, 5200);
});

// Initialisation de la page : vérification des permissions et titre de la page
const initPage = (() => {
    if(! offer.value.canEdit) {
        redirectNotFound();
    } else {

        const titleKey = 'page.admin.coachingOffers.edit.title';
        const descriptionKey = 'page.admin.coachingOffers.edit.description';
        document.title = t(titleKey, {
            name: offer.value.name
        }) + ' - ' + currentApplication.value.name;
        pageTitle.value = t(titleKey, {
            name: offer.value.name
        });
        pageDescription.value = t(descriptionKey, {
            name: offer.value.name
        });

        // Cache le succès de création après quelques secondes
        if(fromCreation.value !== null) {
            onCreationSuccess();
        }
    }
});

// Chargement de l'offre et initialisation de la page
const loadOffer = (() => {

    coachingOfferStore.loadOfferById(offerId).then(() => {
        initPage();
    }).catch((e) => {
        loadingOfferError.value = true;
    }).finally(() => {
        ready.value = true;
    });
});

onMounted(() => {
    if(! authUserHasPermission('edit coaching offer')) {
        redirectNotFound();
    } else {
        loadOffer();
    }
});

// Méthode à exécuter lors de la mise à jour d'une offre
const onUpdateOffer = ((newUser) => {
    coachingOfferStore.updateOffer(offer);
    offerSaved.value = true;

    setTimeout(() => {
        if(updateSuccessMessageRef.value !== null) {
            updateSuccessMessageRef.value.scrollIntoView({ behavior: 'smooth' });
        }
    }, 200);

    setTimeout(() => {
        offerSaved.value = false;
    }, 5000);
});
</script>

<template>
    <layout wrapperContentClasses="wrapper_edit-add">
        <loader v-if="! ready"></loader>
        <template v-else>
            <p
                v-if="loadingOfferError || ! offer"
                class="generic-message generic-message-error"
            >
                {{ $t('page.admin.coachingOffers.edit.loading') }}
            </p>
            <template v-else>
                <div class="content_header">
                    <div class="content-header_title">
                        <h1 class="first-title">{{ pageTitle }}</h1>
                        <p>{{ pageDescription }}</p>
                    </div>
                </div>
                <div class="wrapper_content ">
                    <p
                        v-if="fromCreation"
                        ref="creationSuccessMessageRef"
                        class="generic-message generic-message-success"
                    >
                        {{ $t('page.admin.coachingOffers.edit.created', { name: offer.name }) }}
                    </p>

                    <p
                        v-if="offerSaved"
                        ref="updateSuccessMessageRef"
                        class="generic-message generic-message-success"
                    >
                        {{ $t('page.admin.coachingOffers.edit.updated', { name: offer.name }) }}
                    </p>

                    <edit-form
                        :offer="offer"
                        v-on:offer-save="onUpdateOffer($event)"
                    ></edit-form>
                </div>
            </template>
        </template>
    </layout>
</template>
